/* RESPONSIBLE TEAM: team-reporting */

import { cn, Tooltip } from '@intercom/surge';
import { SmallDownArrowIcon, SmallUpArrowIcon } from '@intercom/surge/icons';
import { Trans } from '@lingui/react/macro';

import {
  buildCounterValue,
  type CounterValue,
} from '@/features/reporting-charts/transforms/counter';
import type {
  ChartConfig,
  ChartDataResponse,
  DateRange,
  RenderContext,
} from '@/features/reporting-charts/types';

export interface CounterProps {
  data: ChartDataResponse;
  config: ChartConfig;
  renderContext: RenderContext;
  dateRange?: DateRange;
  className?: string;
}

export function Counter({ data, config, renderContext, dateRange, className }: CounterProps) {
  const counterValue = buildCounterValue(data, config, renderContext);

  const showComparison =
    config.visualizationOptions?.showTimeComparison &&
    counterValue.absoluteChange !== null &&
    counterValue.changeDirection !== 'none';

  return (
    <div className={cn('flex flex-col justify-end', className)}>
      <div className="flex items-baseline gap-2">
        <span className="text-data-h1 text-default">{counterValue.formattedValue}</span>
        {showComparison && counterValue.formattedAbsoluteChange && (
          <ChangeIndicator
            formattedChange={counterValue.formattedAbsoluteChange}
            changeDirection={counterValue.changeDirection}
            improvementDirection={counterValue.improvementDirection}
            previousPeriod={dateRange?.previous}
            locale={renderContext.locale}
          />
        )}
      </div>
      {counterValue.formattedDataLabel && (
        <div className="text-caption text-muted">{counterValue.formattedDataLabel}</div>
      )}
      {counterValue.formattedTarget && (
        <div className="text-caption text-muted font-semibold">
          <Trans>Target:</Trans> {counterValue.formattedTarget}
        </div>
      )}
    </div>
  );
}

function ChangeIndicator({
  formattedChange,
  changeDirection,
  improvementDirection,
  previousPeriod,
  locale,
}: {
  formattedChange: string;
  changeDirection: CounterValue['changeDirection'];
  improvementDirection: CounterValue['improvementDirection'];
  previousPeriod?: { start: Date; end: Date };
  locale: string;
}) {
  const ArrowIcon = changeDirection === 'up' ? SmallUpArrowIcon : SmallDownArrowIcon;

  const isGoodChange =
    (improvementDirection === 'increasing' && changeDirection === 'up') ||
    (improvementDirection === 'decreasing' && changeDirection === 'down');

  const indicator = (
    <span className={cn('flex items-center gap-1', isGoodChange ? 'text-success' : 'text-error')}>
      <ArrowIcon />
      {formattedChange}
    </span>
  );

  if (!previousPeriod) {
    return indicator;
  }

  const tooltipContent = formatComparisonTooltip(previousPeriod, locale);

  return (
    <Tooltip>
      <Tooltip.Trigger>{indicator}</Tooltip.Trigger>
      <Tooltip.Content>{tooltipContent}</Tooltip.Content>
    </Tooltip>
  );
}

function formatComparisonTooltip(period: { start: Date; end: Date }, locale: string): string {
  const formatter = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' });
  const startFormatted = formatter.format(period.start);
  const endFormatted = formatter.format(period.end);
  return `Compared to ${startFormatted} - ${endFormatted}`;
}
